import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';

import {
  RECENT_RATE_CODES_LIMIT,
  useGetRecentlyCreatedRateCodes,
} from '~/hooks/apis/cruises/useGetRecentlyCreatedRateCodes';

import { Dayjs, parseDayjs } from '~/services/TimeService';

const SHORTCUT_PERIODS = [
  { hours: 24, label: 'Last 24 hours' },
  { hours: 48, label: 'Last 48 hours' },
];
const MAX_CUSTOM_RANGE_HOURS = 48;

interface RecentRateCodesRequest {
  id: number;
  source: 'shortcut' | 'custom';
  shortcutHours?: number;
  createdAtStart: string;
  createdAtEnd?: string;
}

interface Props {
  onChange: (codes: Array<string>) => void;
  resetKey: number;
}

function getCustomRangeError(customStart: Dayjs | null, customEnd: Dayjs | null): string | undefined {
  if (!customStart || !customEnd) {
    return 'Select a start and end date.';
  }
  if (!customStart.isValid() || !customEnd.isValid()) {
    return 'Enter valid dates.';
  }
  if (!customEnd.isAfter(customStart)) {
    return 'The end date must be after the start date.';
  }
  if (customEnd.diff(customStart, 'hour', true) > MAX_CUSTOM_RANGE_HOURS) {
    return `The custom range cannot exceed ${MAX_CUSTOM_RANGE_HOURS} hours.`;
  }
  if (customEnd.isAfter(parseDayjs(new Date()))) {
    return 'The end date cannot be in the future.';
  }
}

function RecentlyCreatedRateCodeSelect({ onChange, resetKey }: Props) {
  const [request, setRequest] = useState<RecentRateCodesRequest | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | 'custom' | null>(null);
  const [isCustomRangeVisible, setIsCustomRangeVisible] = useState(false);
  const [customStart, setCustomStart] = useState<Dayjs | null>(null);
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
  const requestId = useRef(0);
  const appliedRequestId = useRef<number | null>(null);
  const recentRateCodesQuery = useGetRecentlyCreatedRateCodes(
    request ? { createdAtStart: request.createdAtStart, createdAtEnd: request.createdAtEnd } : null,
  );
  const isLoading = !!request && recentRateCodesQuery.isFetching;

  useEffect(() => {
    if (!request || !recentRateCodesQuery.isSuccess || appliedRequestId.current === request.id) {
      return;
    }

    appliedRequestId.current = request.id;
    onChange(recentRateCodesQuery.data.result.map((rate) => rate.code));
  }, [onChange, recentRateCodesQuery.data, recentRateCodesQuery.isSuccess, request]);

  useEffect(() => {
    setRequest(null);
    setSelectedOption(null);
    setIsCustomRangeVisible(false);
  }, [resetKey]);

  const createRequest = (
    source: RecentRateCodesRequest['source'],
    createdAtStart: string,
    createdAtEnd?: string,
    shortcutHours?: number,
  ) => {
    requestId.current += 1;
    setRequest({
      id: requestId.current,
      source,
      shortcutHours,
      createdAtStart,
      createdAtEnd,
    });
  };

  const handleSelectPeriod = (hours: number) => {
    setSelectedOption(hours);
    setIsCustomRangeVisible(false);
    createRequest('shortcut', new Date(Date.now() - hours * 60 * 60 * 1000).toISOString(), undefined, hours);
  };

  const handleShowCustomRange = () => {
    const now = parseDayjs(new Date());
    setCustomStart(now.subtract(MAX_CUSTOM_RANGE_HOURS, 'hour'));
    setCustomEnd(now);
    setSelectedOption('custom');
    setIsCustomRangeVisible(true);
  };

  const customRangeError = getCustomRangeError(customStart, customEnd);

  const handleSelectCustomRange = () => {
    if (customRangeError || !customStart || !customEnd) {
      return;
    }

    createRequest('custom', customStart.toISOString(), customEnd.toISOString());
  };

  const isEmptyResult = recentRateCodesQuery.isSuccess && recentRateCodesQuery.data.result.length === 0;
  const isLimitedResult = recentRateCodesQuery.isSuccess && recentRateCodesQuery.data.total > RECENT_RATE_CODES_LIMIT;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {SHORTCUT_PERIODS.map((period) => {
          const isSelectedPeriodLoading = isLoading && request?.shortcutHours === period.hours;

          return (
            <Button
              key={period.hours}
              variant={selectedOption === period.hours ? 'contained' : 'outlined'}
              disabled={isLoading}
              onClick={() => handleSelectPeriod(period.hours)}
              startIcon={isSelectedPeriodLoading ? <CircularProgress size={16} /> : undefined}
            >
              {period.label}
            </Button>
          );
        })}
        <Button
          variant={selectedOption === 'custom' ? 'contained' : 'outlined'}
          disabled={isLoading}
          onClick={handleShowCustomRange}
        >
          Custom
        </Button>
      </Stack>
      {isCustomRangeVisible && (
        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap alignItems="center">
          <DateTimePicker
            label="Start date"
            value={customStart}
            onChange={setCustomStart}
            disableFuture
            slotProps={{ textField: { size: 'small' } }}
          />
          <DateTimePicker
            label="End date"
            value={customEnd}
            onChange={setCustomEnd}
            disableFuture
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="contained"
            disabled={isLoading || !!customRangeError}
            onClick={handleSelectCustomRange}
            startIcon={isLoading && request?.source === 'custom' ? <CircularProgress size={16} /> : undefined}
          >
            Select rates
          </Button>
        </Stack>
      )}
      {isCustomRangeVisible && customRangeError && <Alert severity="error">{customRangeError}</Alert>}
      {recentRateCodesQuery.isError && <Alert severity="error">Unable to load recently created rates.</Alert>}
      {isEmptyResult && <Alert severity="info">No rates were created in the selected period.</Alert>}
      {isLimitedResult && (
        <Alert severity="warning">Only the {RECENT_RATE_CODES_LIMIT} most recently created rates were selected.</Alert>
      )}
    </Stack>
  );
}

export default RecentlyCreatedRateCodeSelect;
